import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Wrench, Play, Map, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import { getIcon } from "@/lib/icons";

export function UniversalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); setRes(null); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setRes(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q: term } });
        setRes(data);
      } catch { setRes(null); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => { setOpen(false); navigate(path); };
  const hasResults = res && (res.topics.length || res.tools.length || res.videos.length);

  return (
    <>
      <button
        data-testid="search-trigger"
        onClick={() => setOpen(true)}
        aria-label="Search"
        className="hidden lg:flex items-center gap-2 h-9 w-52 px-3.5 rounded-full border border-border bg-card/60 text-muted-foreground text-sm hover:border-primary/40 hover:text-foreground transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search anything…</span>
        <kbd className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-secondary">⌘K</kbd>
      </button>
      <button
        data-testid="search-trigger-mobile"
        onClick={() => setOpen(true)}
        aria-label="Search"
        className="lg:hidden grid place-items-center h-9 w-9 rounded-full hover:bg-accent transition-colors"
      >
        <Search className="h-5 w-5 text-muted-foreground" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 top-[18%] translate-y-0 max-w-xl" data-testid="search-dialog">
          <DialogTitle className="sr-only">Search topics, tools and videos</DialogTitle>
          <DialogDescription className="sr-only">Jump directly to any topic, AI tool or video</DialogDescription>
          <Command shouldFilter={false} className="bg-transparent">
            <CommandInput
              data-testid="search-input"
              placeholder="Jump to any topic, tool or video…"
              value={q}
              onValueChange={setQ}
            />
            <CommandList className="max-h-[55vh]">
              {loading && (
                <div className="py-6 grid place-items-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!loading && q.trim().length >= 2 && !hasResults && (
                <CommandEmpty data-testid="search-empty">No matches — try another word.</CommandEmpty>
              )}
              {!loading && q.trim().length < 2 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Type at least 2 characters — topics, AI tools, or specific videos.
                </p>
              )}
              {!loading && res?.topics?.length > 0 && (
                <CommandGroup heading="Topics in the skill graph">
                  {res.topics.map((t) => {
                    const Icon = getIcon(t.icon) || Map;
                    return (
                      <CommandItem key={`topic-${t.slug}`} value={`topic-${t.slug}`} data-testid={`search-topic-${t.slug}`}
                        onSelect={() => go(t.in_path ? `/dashboard?open=${t.slug}` : (t.video_id ? `/lesson/${t.video_id}` : "/dashboard"))}
                        className="gap-3 cursor-pointer">
                        <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary/10 text-primary shrink-0"><Icon className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold text-sm truncate">{t.title}</span>
                          <span className="block text-xs text-muted-foreground truncate">{t.level_label}</span>
                        </span>
                        {t.in_path && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">IN YOUR PATH</span>}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {!loading && res?.tools?.length > 0 && (
                <CommandGroup heading="AI tools">
                  {res.tools.map((t) => {
                    const Icon = getIcon(t.icon) || Wrench;
                    return (
                      <CommandItem key={`tool-${t.slug}`} value={`tool-${t.slug}`} data-testid={`search-tool-${t.slug}`}
                        onSelect={() => go(`/tools/course?tools=${t.slug}`)} className="gap-3 cursor-pointer">
                        <span className="grid place-items-center h-8 w-8 rounded-lg bg-violet-100 text-violet-600 shrink-0"><Icon className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold text-sm truncate">{t.name}</span>
                          <span className="block text-xs text-muted-foreground truncate">{t.tagline}</span>
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {!loading && res?.videos?.length > 0 && (
                <CommandGroup heading="Videos">
                  {res.videos.map((v) => (
                    <CommandItem key={`video-${v.video_id}`} value={`video-${v.video_id}`} data-testid={`search-video-${v.video_id}`}
                      onSelect={() => go(`/lesson/${v.video_id}`)} className="gap-3 cursor-pointer">
                      {v.thumbnail
                        ? <img src={v.thumbnail} alt="" className="h-9 w-14 rounded-md object-cover shrink-0" />
                        : <span className="grid place-items-center h-9 w-14 rounded-md bg-secondary shrink-0"><Play className="h-4 w-4" /></span>}
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-sm truncate">{v.title}</span>
                        <span className="block text-xs text-muted-foreground truncate">{v.channel}{v.duration_label ? ` · ${v.duration_label}` : ""}</span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
